import { Button } from 'finara-components';
import { useState } from 'react';

import {
  PaymentData as PaymentDataType,
  PaymentSummary,
  ExecutionType,
  PaymentBeneficiary,
} from '@/types/payment';

interface PaymentDataProps {
  onSubmit: (data: PaymentDataType, summary: PaymentSummary) => void;
  /**
   * Prefilled values (e.g. a "Depositar" deposit prefills the beneficiary). The
   * fields remain editable.
   */
  initialData?: PaymentDataType;
}

export const PaymentData: React.FC<PaymentDataProps> = ({ onSubmit, initialData }) => {
  const [formData, setFormData] = useState<PaymentDataType>({
    beneficiary: {
      fullName: initialData?.beneficiary.fullName ?? '',
      rfc: initialData?.beneficiary.rfc ?? '',
      clabe: initialData?.beneficiary.clabe ?? '',
      email: initialData?.beneficiary.email ?? '',
    },
    amount: initialData?.amount ?? 0,
    concept: initialData?.concept ?? '',
    executionType: initialData?.executionType ?? 'immediate',
    scheduledDate: initialData?.scheduledDate ?? '',
    scheduledTime: initialData?.scheduledTime ?? '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.beneficiary.fullName.trim()) {
      newErrors['fullName'] = 'El nombre completo es requerido';
    }

    // RFC is optional, but if provided, must be valid
    if (
      formData.beneficiary.rfc.trim() &&
      !/^[A-ZÑ\x26]{3,4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1])([A-Z]|[0-9]){2}([A]|[0-9]){1}$/.test(
        formData.beneficiary.rfc
      )
    ) {
      newErrors['rfc'] = 'El RFC es inválido';
    }

    if (!formData.beneficiary.clabe.trim()) {
      newErrors['clabe'] = 'La CLABE es requerida';
    } else if (formData.beneficiary.clabe.length !== 18) {
      newErrors['clabe'] = 'La CLABE debe tener 18 dígitos';
    }

    // Email is optional, but if provided, must be valid
    if (
      formData.beneficiary.email?.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.beneficiary.email)
    ) {
      newErrors['email'] = 'El email es inválido';
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors['amount'] = 'El monto debe ser mayor a 0';
    }

    if (!formData.concept.trim()) {
      newErrors['concept'] = 'El concepto es requerido';
    }

    if (formData.executionType === 'scheduled') {
      if (!formData.scheduledDate) {
        newErrors['scheduledDate'] = 'La fecha es requerida para pagos programados';
      }
      if (!formData.scheduledTime) {
        newErrors['scheduledTime'] = 'La hora es requerida para pagos programados';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // Calculate summary
      const summary: PaymentSummary = {
        paymentAmount: formData.amount,
        total: formData.amount,
      };

      onSubmit(formData, summary);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    if (field.startsWith('beneficiary.')) {
      const beneficiaryField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        beneficiary: {
          ...prev.beneficiary,
          [beneficiaryField as keyof PaymentBeneficiary]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-brand-white mb-2">Datos del Pago</h2>
        <p className="text-muted">Ingresa la información del beneficiario y detalles del pago</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Beneficiary Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="beneficiary-fullName"
              className="block text-sm font-medium text-brand-white mb-2"
            >
              Beneficiario (Nombre completo) *
            </label>
            <input
              id="beneficiary-fullName"
              type="text"
              value={formData.beneficiary.fullName}
              onChange={e => handleInputChange('beneficiary.fullName', e.target.value)}
              placeholder="Nombre completo del beneficiario"
              className={`w-full px-3 py-2 border rounded-md bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue ${
                errors['fullName'] ? 'border-red-500' : 'border-border'
              }`}
            />
            {errors['fullName'] && (
              <p className="mt-1 text-sm text-red-400">{errors['fullName']}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="beneficiary-clabe"
              className="block text-sm font-medium text-brand-white mb-2"
            >
              CLABE Destino (18 dígitos) *
            </label>
            <input
              id="beneficiary-clabe"
              type="number"
              value={formData.beneficiary.clabe}
              onChange={e => handleInputChange('beneficiary.clabe', e.target.value)}
              placeholder="123456789012345678"
              minLength={18}
              maxLength={18}
              className={`w-full px-3 py-2 border rounded-md bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue ${
                errors['clabe'] ? 'border-red-500' : 'border-border'
              }`}
            />
            {errors['clabe'] && <p className="mt-1 text-sm text-red-400">{errors['clabe']}</p>}
          </div>

          <div>
            <label
              htmlFor="beneficiary-rfc"
              className="block text-sm font-medium text-brand-white mb-2"
            >
              RFC del Beneficiario (Opcional)
            </label>
            <input
              id="beneficiary-rfc"
              type="text"
              value={formData.beneficiary.rfc}
              onChange={e => handleInputChange('beneficiary.rfc', e.target.value.toUpperCase())}
              placeholder="XAXX010101000"
              maxLength={13}
              className={`w-full px-3 py-2 border rounded-md bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue ${
                errors['rfc'] ? 'border-red-500' : 'border-border'
              }`}
            />
            {errors['rfc'] && <p className="mt-1 text-sm text-red-400">{errors['rfc']}</p>}
          </div>

          <div>
            <label
              htmlFor="beneficiary-email"
              className="block text-sm font-medium text-brand-white mb-2"
            >
              Email del Beneficiario (Opcional)
            </label>
            <input
              id="beneficiary-email"
              type="email"
              value={formData.beneficiary.email || ''}
              onChange={e => handleInputChange('beneficiary.email', e.target.value)}
              placeholder="beneficiario@ejemplo.com"
              className={`w-full px-3 py-2 border rounded-md bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue ${
                errors['email'] ? 'border-red-500' : 'border-border'
              }`}
            />
            {errors['email'] && <p className="mt-1 text-sm text-red-400">{errors['email']}</p>}
          </div>

          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-brand-white mb-2">
              Cantidad (MXN) *
            </label>
            <input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              value={formData.amount || ''}
              onChange={e => handleInputChange('amount', parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              className={`w-full px-3 py-2 border rounded-md bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue ${
                errors['amount'] ? 'border-red-500' : 'border-border'
              }`}
            />
            {errors['amount'] && <p className="mt-1 text-sm text-red-400">{errors['amount']}</p>}
          </div>
        </div>

        {/* Concept */}
        <div>
          <label htmlFor="concept" className="block text-sm font-medium text-brand-white mb-2">
            Concepto / Referencia *
          </label>
          <textarea
            value={formData.concept}
            onChange={e => handleInputChange('concept', e.target.value)}
            placeholder="Descripción del pago"
            rows={3}
            className={`w-full px-3 py-2 border rounded-md bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue ${
              errors['concept'] ? 'border-red-500' : 'border-border'
            }`}
          />
          {errors['concept'] && <p className="mt-1 text-sm text-red-400">{errors['concept']}</p>}
        </div>

        {/* Execution Type */}
        <div>
          <label htmlFor="executionType" className="block text-sm font-medium text-brand-white mb-3">
            Fecha de Ejecución
          </label>
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                id="executionType"
                type="radio"
                value="immediate"
                checked={formData.executionType === 'immediate'}
                onChange={e => handleInputChange('executionType', e.target.value as ExecutionType)}
                className="h-4 w-4 text-brand-blue border-border focus:ring-brand-blue"
              />
              <label htmlFor="immediate" className="ml-3 text-sm text-brand-white">
                Ejecutar ahora
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="scheduled"
                value="scheduled"
                checked={formData.executionType === 'scheduled'}
                onChange={e => handleInputChange('executionType', e.target.value as ExecutionType)}
                className="h-4 w-4 text-brand-blue border-border focus:ring-brand-blue"
              />
              <label htmlFor="scheduled" className="ml-3 text-sm text-brand-white">
                Programar
              </label>
            </div>
          </div>
        </div>

        {/* Scheduled Date and Time */}
        {formData.executionType === 'scheduled' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="scheduledDate"
                className="block text-sm font-medium text-brand-white mb-2"
              >
                Fecha
              </label>
              <input
                type="date"
                value={formData.scheduledDate}
                onChange={e => handleInputChange('scheduledDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 border rounded-md bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue ${
                  errors['scheduledDate'] ? 'border-red-500' : 'border-border'
                }`}
              />
              {errors['scheduledDate'] && (
                <p className="mt-1 text-sm text-red-400">{errors['scheduledDate']}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="scheduledTime"
                className="block text-sm font-medium text-brand-white mb-2"
              >
                Hora
              </label>
              <input
                type="time"
                value={formData.scheduledTime}
                onChange={e => handleInputChange('scheduledTime', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue ${
                  errors['scheduledTime'] ? 'border-red-500' : 'border-border'
                }`}
              />
              {errors['scheduledTime'] && (
                <p className="mt-1 text-sm text-red-400">{errors['scheduledTime']}</p>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-end pt-4">
          <Button type="submit" variant="black" size="large" className="min-w-32">
            Siguiente
          </Button>
        </div>
      </form>
    </div>
  );
};
